export enum HardwareDeviceType {
  Printer = 0,
  CashDrawer = 1,
}

export enum HardwareConnectionType {
  Printer = 0,
  Usb = 1,
}

export enum PrinterType {
  Epson = 'epson',
  Boca = 'boca',
}

export const PrinterTypeTranslations: Record<PrinterType, string> = {
  [PrinterType.Epson]: 'printer.form.field.printer_type_epson',
  [PrinterType.Boca]: 'printer.form.field.printer_type_boca',
};

export enum PrinterOfflineReason {
  Offline = 'printer_is_offline',
  OfflineOrOutOfPaper = 'printer_is_offline_or_out_of_paper',
}

/**
 * The Epson ASB (Automatic Status Back) flags the backend reports, snake_cased as they arrive on
 * the wire. The list is open, so a flag missing here renders as its raw value rather than being
 * dropped.
 */
export const PrinterStatusFlagTranslations: Record<string, string> = {
  drawer_kick3: 'printer.status.flag.drawer_kick3',
  offline: 'printer.status.flag.offline',
  cover_open: 'printer.status.flag.cover_open',
  feeding_by_switch: 'printer.status.flag.feeding_by_switch',
  waiting_for_online_recovery: 'printer.status.flag.waiting_for_online_recovery',
  feed_switch_pressed: 'printer.status.flag.feed_switch_pressed',
  mechanical_error: 'printer.status.flag.mechanical_error',
  auto_cutter_error: 'printer.status.flag.auto_cutter_error',
  unrecoverable_error: 'printer.status.flag.unrecoverable_error',
  auto_recovery_error: 'printer.status.flag.auto_recovery_error',
  paper_near_end: 'printer.status.flag.paper_near_end',
  paper_end: 'printer.status.flag.paper_end',
  waiting_for_label_removal: 'printer.status.flag.waiting_for_label_removal',
  label_peeling_sensor_empty: 'printer.status.flag.label_peeling_sensor_empty',
};

/**
 * Flags support can ask the cinema to physically fix. The rest (drawer contact, feed button, label
 * peeler) are normal operating state that an Epson reports constantly, so they stay visible but
 * quiet instead of shouting alongside a real outage.
 */
export const ActionablePrinterStatusFlags: ReadonlySet<string> = new Set([
  'offline',
  'cover_open',
  'paper_near_end',
  'paper_end',
  'mechanical_error',
  'auto_cutter_error',
  'unrecoverable_error',
  'auto_recovery_error',
  'waiting_for_online_recovery',
]);

export interface HardwareDevice {
  id: string;
  shop_id?: string;
  name: string;
  /**
   * The identifier printed on the physical device - an Epson serial, an asset sticker. Optional,
   * free text, and deliberately not unique: it identifies hardware, not a row. Returned everywhere
   * `name` is. See PAUL-3536.
   */
  tag?: string | null;
  username?: string | null;
  password?: string | null;
  point_of_sale_id?: string;
  type: HardwareDeviceType;
  connection_type?: HardwareConnectionType;
  connected_printer_device_id?: string;
  usb_config_json?: string;
  printer_type?: PrinterType | null;
  template_key?: string | null;
  receipt_format_version?: number | null;
  ticket_template_key?: string | null;
  kitchen_receipt_template_key?: string | null;
  financial_report_template_key?: string | null;

  // Computed server-side and ignored on POST/PUT - never send these back.
  // `online: null` means "no status feed" (Boca printers and cash drawers never report), not offline.
  online?: boolean | null;
  offline_reason?: PrinterOfflineReason | string | null;
  /** ISO 8601, UTC. `null` = never reported. */
  last_seen_at?: string | null;
  status_flags?: string[];
}
